import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { SettingsDepartment, SettingsSample } from '../sampleData';
import type { DetailsPatch, DetailsValues } from '../state';

import { FIELD_HEIGHT_SX, FormField } from './FormField';

/**
 * Where it belongs, edited (brief §3): four selects over the sample's lists,
 * with no heading of its own (Mette, 3 Nov). Resource planning moved to When (decision 18).
 */
export type BelongsFormProps = {
  draft: Pick<DetailsValues, 'legalEntityId' | 'departmentId' | 'projectType' | 'projectCategory'>;
  sample: SettingsSample;
  onChange: (patch: DetailsPatch) => void;
};

/** Each department with its depth under the root, in tree order — walked iteratively. */
const departmentOptions = (
  departments: SettingsDepartment[],
): { department: SettingsDepartment; depth: number }[] => {
  const options: { department: SettingsDepartment; depth: number }[] = [];
  const stack = departments
    .filter((department) => department.parentId === null)
    .reverse()
    .map((department) => ({ department, depth: 0 }));
  while (stack.length > 0) {
    const entry = stack.pop()!;
    options.push(entry);
    const children = departments.filter((child) => child.parentId === entry.department.id);
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push({ department: children[i]!, depth: entry.depth + 1 });
    }
  }
  return options;
};

export const BelongsForm = ({ draft, sample, onChange }: BelongsFormProps) => {
  const { t } = useTranslation('pmDetails');
  const notSet = (
    <Typography component="span" variant="bodyRegular" sx={{ color: 'var(--color-fog-80)' }}>
      {t('belongs.NotSet')}
    </Typography>
  );
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 2,
        }}
      >
        <FormField label={t('belongs.LegalEntity')} htmlFor="details-legal-entity">
          <Select
            size="small"
            sx={FIELD_HEIGHT_SX}
            id="details-legal-entity"
            value={draft.legalEntityId}
            onChange={(event) => onChange({ legalEntityId: event.target.value })}
            data-automation-id="details-legal-entity"
          >
            {sample.legalEntities.map((entity) => (
              <MenuItem key={entity.id} value={entity.id}>
                {entity.name}
              </MenuItem>
            ))}
          </Select>
        </FormField>
        <FormField label={t('belongs.Department')} htmlFor="details-department">
          <Select
            size="small"
            sx={FIELD_HEIGHT_SX}
            id="details-department"
            value={draft.departmentId}
            onChange={(event) => onChange({ departmentId: event.target.value })}
            renderValue={(id) => sample.departments.find((d) => d.id === id)?.name ?? ''}
            data-automation-id="details-department"
          >
            {departmentOptions(sample.departments).map(({ department, depth }) => (
              <MenuItem key={department.id} value={department.id} sx={{ pl: 1.5 + depth * 2 }}>
                {department.name}
              </MenuItem>
            ))}
          </Select>
        </FormField>
        <FormField label={t('belongs.ProjectType')} htmlFor="details-project-type">
          <Select
            size="small"
            sx={FIELD_HEIGHT_SX}
            id="details-project-type"
            displayEmpty
            value={draft.projectType ?? ''}
            onChange={(event) => onChange({ projectType: event.target.value || null })}
            renderValue={(value) => value || notSet}
            data-automation-id="details-project-type"
          >
            <MenuItem value="">{t('belongs.NotSet')}</MenuItem>
            {sample.projectTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormField>
        <FormField label={t('belongs.Category')} htmlFor="details-category">
          <Select
            size="small"
            sx={FIELD_HEIGHT_SX}
            id="details-category"
            displayEmpty
            value={draft.projectCategory ?? ''}
            onChange={(event) => onChange({ projectCategory: event.target.value || null })}
            renderValue={(value) => value || notSet}
            data-automation-id="details-category"
          >
            <MenuItem value="">{t('belongs.NotSet')}</MenuItem>
            {sample.projectCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormField>
      </Box>
    </Stack>
  );
};
